// Web mirror of iOS Core/QuestionSetProgressStore.swift, backed by the shared
// /learner/progress blob. Two-way sync design (see plan):
//   - READ: hydrate the iOS blob; expose per-set wrong pools / completed bulks /
//     cursor / outcomes so the composer can drill real wrongs + skip mastered work.
//   - WRITE: mutate the *preserved* raw blob in place (never rebuild it from
//     scratch) so iOS-only / future fields round-trip untouched, then debounced
//     PUT. Dates use ISO8601 WITHOUT millis to satisfy Swift's .iso8601 decoder.
//     The server now MERGES each PUT per-set/per-question (non-destructive) and
//     returns the merged blob, which we adopt — so web + iOS reach parity for
//     all sets and neither clobbers the other.
import { getProgress, putProgress } from "@/api/learner";

/** Two-way write to the shared /learner/progress blob. Safe because:
 *  (1) preserve-unknown — untouched sets round-trip byte-identical (we keep the
 *      raw parsed object and only mutate specific nested fields);
 *  (2) any set entry the web creates includes every field iOS's decoder
 *      hard-requires (completedBulkIds, questionOutcomes, seenInfoItemIds,
 *      wrongQuestionIds, startedAt, lastActivityAt, attemptCount, correctCount);
 *  (3) dates are ISO8601 without millis (Swift .iso8601 rejects fractional secs);
 *  (4) the server merge is non-destructive, so even a stale PUT can't lose data.
 *  Kill-switch: set false to instantly disable writes. */
export const WRITE_ENABLED = true;

interface RawOutcome {
  outcome?: "correct" | "wrong" | "unanswered";
  lastAnsweredAt?: string | null;
}
interface RawCurrentPass {
  completedBulkIds?: string[];
  cursor?: { bulkId: string; lastQuestionId: string; lastTouchedAt?: string } | null;
  questionOutcomes?: Record<string, RawOutcome>;
  cumulativeOutcomes?: Record<string, RawOutcome>;
  seenInfoItemIds?: string[];
  wrongQuestionIds?: string[];
  startedAt?: string;
  lastActivityAt?: string;
  attemptCount?: number;
  correctCount?: number;
  [key: string]: unknown;
}
/** Only the fields the Library reads off an archived pass; preserve-unknown holds. */
interface RawPastPass {
  attemptCount?: number;
  correctCount?: number;
  [key: string]: unknown;
}
interface RawSetProgress {
  inPool?: boolean;
  currentPass?: RawCurrentPass | null;
  pastPasses?: RawPastPass[];
  [key: string]: unknown;
}
type Blob = Record<string, RawSetProgress>;

/** Lifetime totals folded across the current pass + every archived pass (iOS parity). */
export interface LifetimeStats {
  attemptCount: number;
  correctCount: number;
  accuracyPercent: number | null;
}

/** ISO8601 without fractional seconds (Swift .iso8601 rejects millis). */
function isoNow(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
}

class ProgressStore {
  private blob: Blob = {};
  private hydrated = false;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  async hydrate(force = false): Promise<void> {
    if (this.hydrated && !force) return;
    try {
      const mirror = await getProgress();
      this.blob = (mirror.state as Blob) ?? {};
      // Only mark hydrated on a SUCCESSFUL read. If the GET throws we leave
      // `hydrated` false (and `blob` untouched) so a later flush() can't
      // overwrite the server with an empty/default blob we never loaded.
      this.hydrated = true;
    } catch {
      /* read failed — stay un-hydrated; flush() is guarded on hydrated. */
    }
  }

  private pass(setId: string): RawCurrentPass | null {
    return this.blob[setId]?.currentPass ?? null;
  }

  // ── Read accessors (drive the composer) ───────────────────────────────────
  wrongIds(setId: string): Set<string> {
    return new Set(this.pass(setId)?.wrongQuestionIds ?? []);
  }
  completedBulkIds(setId: string): Set<string> {
    return new Set(this.pass(setId)?.completedBulkIds ?? []);
  }
  seenInfo(setId: string): Set<string> {
    return new Set(this.pass(setId)?.seenInfoItemIds ?? []);
  }
  cursor(setId: string): { bulkId: string; lastQuestionId: string } | null {
    const c = this.pass(setId)?.cursor;
    return c ? { bulkId: c.bulkId, lastQuestionId: c.lastQuestionId } : null;
  }
  isCorrect(setId: string, qid: string): boolean {
    return this.pass(setId)?.questionOutcomes?.[qid]?.outcome === "correct";
  }
  /** Recorded outcome for a question, or null if never attempted. Drives the
   *  per-question done/not-done marker + the gated answer reveal in Library. */
  outcomeOf(setId: string, qid: string): "correct" | "wrong" | null {
    const o = this.pass(setId)?.questionOutcomes?.[qid]?.outcome;
    return o === "correct" || o === "wrong" ? o : null;
  }
  /** True once the learner has answered this question (correct or wrong). */
  isAttempted(setId: string, qid: string): boolean {
    return this.outcomeOf(setId, qid) !== null;
  }
  /** Has the learner touched this set at all? Drives the "In progress" pill. */
  hasStarted(setId: string): boolean {
    const p = this.pass(setId);
    return !!p && ((p.attemptCount ?? 0) > 0 || !!p.cursor);
  }
  completedBulkCount(setId: string): number {
    return this.pass(setId)?.completedBulkIds?.length ?? 0;
  }
  attemptCount(setId: string): number {
    return this.pass(setId)?.attemptCount ?? 0;
  }
  correctCount(setId: string): number {
    return this.pass(setId)?.correctCount ?? 0;
  }
  /** Last time the learner answered/touched this set (ISO8601), or null. Drives
   *  the "Practised …" label. iOS parity: `QuestionSetProgress.lastPlayedAt`. */
  lastActivityAt(setId: string): string | null {
    return this.pass(setId)?.lastActivityAt ?? null;
  }
  /** How many of a bulk's graded items are currently marked correct, for the
   *  per-bulk "x/y" fraction in the set detail. `gradedIds` is the bulk's
   *  sequence with card/info items already filtered out by the caller. */
  bulkOutcomeCounts(setId: string, gradedIds: string[]): { correct: number; total: number } {
    const outcomes = this.pass(setId)?.questionOutcomes;
    let correct = 0;
    if (outcomes) {
      for (const id of gradedIds) {
        if (outcomes[id]?.outcome === "correct") correct += 1;
      }
    }
    return { correct, total: gradedIds.length };
  }
  /** Lifetime correct/attempts folding the current pass + all past passes (iOS parity). */
  lifetimeStats(setId: string): LifetimeStats {
    const sp = this.blob[setId];
    const p = sp?.currentPass;
    let attempts = p?.attemptCount ?? 0;
    let correct = p?.correctCount ?? 0;
    for (const past of sp?.pastPasses ?? []) {
      attempts += past?.attemptCount ?? 0;
      correct += past?.correctCount ?? 0;
    }
    return {
      attemptCount: attempts,
      correctCount: correct,
      accuracyPercent: attempts > 0 ? Math.round((correct / attempts) * 100) : null,
    };
  }
  /** Number of completed runs — drives the "Mastered N×" badge. */
  sessionsCompleted(setId: string): number {
    return this.blob[setId]?.pastPasses?.length ?? 0;
  }

  // ── Write side (guarded; preserve-unknown) ────────────────────────────────
  private ensurePass(setId: string): RawCurrentPass {
    let sp = this.blob[setId];
    if (!sp) {
      sp = { inPool: true, currentPass: null, pastPasses: [] };
      this.blob[setId] = sp;
    }
    if (!sp.currentPass) {
      const now = isoNow();
      // Every field iOS CurrentPass.init(from:) hard-requires must be present.
      sp.currentPass = {
        completedBulkIds: [],
        cursor: null,
        questionOutcomes: {},
        cumulativeOutcomes: {},
        seenInfoItemIds: [],
        wrongQuestionIds: [],
        startedAt: now,
        lastActivityAt: now,
        attemptCount: 0,
        correctCount: 0,
      };
    }
    return sp.currentPass;
  }

  recordOutcome(
    setId: string,
    qid: string,
    correct: boolean,
    ctx: { bulkId: string; bulkGradedSequence: string[] },
  ): void {
    if (!WRITE_ENABLED) return;
    const p = this.ensurePass(setId);
    const prev = p.questionOutcomes?.[qid]?.outcome;
    const outcome: RawOutcome = { outcome: correct ? "correct" : "wrong", lastAnsweredAt: isoNow() };
    p.questionOutcomes = p.questionOutcomes ?? {};
    p.questionOutcomes[qid] = outcome;
    // cumulativeOutcomes mirrors questionOutcomes but is never pruned (iOS parity).
    p.cumulativeOutcomes = p.cumulativeOutcomes ?? {};
    p.cumulativeOutcomes[qid] = outcome;
    p.attemptCount = (p.attemptCount ?? 0) + 1;
    if (correct) p.correctCount = (p.correctCount ?? 0) + 1;

    const wrong = new Set(p.wrongQuestionIds ?? []);
    if (correct) wrong.delete(qid);
    else wrong.add(qid);
    p.wrongQuestionIds = [...wrong];

    if (prev !== "correct" && correct) {
      // Bulk completion: every graded q in this bulk now correct.
      const allDone = ctx.bulkGradedSequence.every(
        (id) => p.questionOutcomes?.[id]?.outcome === "correct",
      );
      if (allDone && ctx.bulkGradedSequence.length > 0) {
        const done = new Set(p.completedBulkIds ?? []);
        done.add(ctx.bulkId);
        p.completedBulkIds = [...done];
      }
    }
    p.cursor = { bulkId: ctx.bulkId, lastQuestionId: qid, lastTouchedAt: isoNow() };
    p.lastActivityAt = isoNow();
    this.scheduleFlush();
  }

  recordInfoSeen(setId: string, itemId: string): void {
    if (!WRITE_ENABLED) return;
    const p = this.ensurePass(setId);
    const seen = new Set(p.seenInfoItemIds ?? []);
    seen.add(itemId);
    p.seenInfoItemIds = [...seen];
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (!WRITE_ENABLED) return;
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => void this.flush(), 5000);
  }

  async flush(): Promise<void> {
    if (!WRITE_ENABLED) return;
    // Never overwrite the server before a successful hydrate — otherwise a
    // flush triggered in the post-login window (e.g. visibilitychange) would
    // PUT the empty default blob over the user's real, not-yet-loaded progress
    // (last-writer-wins). This is the key data-loss guard.
    if (!this.hydrated) return;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    try {
      // Adopt the server's MERGED blob so we pick up the other device's
      // progress in the same round-trip. Safe even if a local write raced the
      // PUT: the merge is non-destructive, so the next debounced flush re-sends
      // our delta and the server folds it back in (converges within a cycle).
      const mirror = await putProgress(this.blob as Record<string, unknown>);
      this.blob = (mirror.state as Blob) ?? this.blob;
    } catch {
      /* best-effort, like iOS */
    }
  }

  /** Drop all in-memory state and the hydrated flag. Call on logout so the
   *  next login re-fetches from the server (the singleton outlives a logout,
   *  so without this a re-login keeps the previous user's blob and `hydrate()`
   *  early-returns on the stale flag). Cancels any pending debounced flush —
   *  callers should `await flush()` first if the tail must be persisted. */
  reset(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.blob = {};
    this.hydrated = false;
  }
}

export const progressStore = new ProgressStore();
